/**
 * Web chat widgets — the embeddable website chat channel. Drives the
 * Settings → Channels "Web Chat" management section (create a widget, copy its
 * embed snippet, rename/archive).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messagingKeys } from '@/lib/queryKeys';
import { webchatWidgetsApi } from './api';
import type { CreateWebChatWidgetPayload, UpdateWebChatWidgetPayload } from './types';

export function useWebChatWidgets() {
  return useQuery({
    queryKey: messagingKeys.webchatWidgets(),
    queryFn: () => webchatWidgetsApi.list(),
    staleTime: 30_000,
  });
}

export function useCreateWebChatWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWebChatWidgetPayload) => webchatWidgetsApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.webchatWidgets() });
      // A new widget materialises a channel Inbox — refresh the channel list too.
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
    },
  });
}

export function useUpdateWebChatWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateWebChatWidgetPayload) =>
      webchatWidgetsApi.update(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.webchatWidgets() });
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
    },
  });
}

/** Rotate a widget's public key. Invalidates every existing embed snippet. */
export function useRotateWebChatKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webchatWidgetsApi.rotateKey(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.webchatWidgets() });
    },
  });
}

/**
 * Mint a fresh identity signing secret. The response carries the secret and is
 * the only time it is ever disclosed, so the caller must surface it immediately.
 */
export function useRotateWebChatIdentitySecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webchatWidgetsApi.rotateIdentitySecret(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.webchatWidgets() });
    },
  });
}

export function useDeleteWebChatWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webchatWidgetsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingKeys.webchatWidgets() });
      void qc.invalidateQueries({ queryKey: messagingKeys.inboxes() });
    },
  });
}

/** Fetch the ready-to-paste embed snippet for a widget (lazy — enabled flag). */
export function useWebChatEmbed(id: string | null) {
  return useQuery({
    queryKey: id ? messagingKeys.webchatEmbed(id) : ['webchat-embed', 'none'],
    queryFn: () => webchatWidgetsApi.embed(id as string),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });
}
